import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_AUTH_API_URL || '/api/auth';

// Create axios instance with base configuration
const authAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
authAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
authAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          localStorage.setItem('access_token', data.access_token);
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;

          return authAPI(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth service methods
export const authService = {
  // Login
  async login(email, password) {
    const { data } = await authAPI.post('/auth/login', { email, password });
    
    // Store tokens
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    return data;
  },

  // Logout
  async logout() {
    const refreshToken = localStorage.getItem('refresh_token');
    
    try {
      if (refreshToken) {
        await authAPI.post('/auth/logout', null, {
          params: { refresh_token: refreshToken }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
    }
  },

  // Get current user
  async getCurrentUser() {
    const { data } = await authAPI.get('/auth/me');
    localStorage.setItem('user', JSON.stringify(data));
    return data;
  },

  // Get user permissions
  async getPermissions() {
    const { data } = await authAPI.get('/auth/me/permissions');
    localStorage.setItem('permissions', JSON.stringify(data.permissions));
    return data.permissions;
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  },

  // Get stored user
  getStoredUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Get stored permissions
  getStoredPermissions() {
    const permsStr = localStorage.getItem('permissions');
    return permsStr ? JSON.parse(permsStr) : [];
  },

  // Change password
  async changePassword(currentPassword, newPassword) {
    const { data } = await authAPI.post('/users/me/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return data;
  },

  // User management (Admin only)
  async listUsers(page = 1, pageSize = 20, role = null) {
    const params = { page, page_size: pageSize };
    if (role) params.role = role;
    
    const { data } = await authAPI.get('/users', { params });
    return data;
  },

  async createUser(userData) {
    const { data } = await authAPI.post('/users', userData);
    return data;
  },

  async updateUser(userId, userData) {
    const { data } = await authAPI.patch(`/users/${userId}`, userData);
    return data;
  },

  async deleteUser(userId) {
    const { data } = await authAPI.delete(`/users/${userId}`);
    return data;
  },

  async getUser(userId) {
    const { data } = await authAPI.get(`/users/${userId}`);
    return data;
  },

  // Audit logs (Admin only)
  async getAuditLogs(page = 1, pageSize = 50, userId = null, action = null) {
    const params = { page, page_size: pageSize };
    if (userId) params.user_id = userId;
    if (action) params.action = action;
    
    const { data } = await authAPI.get('/audit', { params });
    return data;
  },
};

export default authAPI;
